import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classnames from 'classnames';
import noop from 'lodash/noop';
import themeable from 'zignal-landscape/lib/themeable';

import { convertMomentToDatePickerTime } from 'helpers/timeFormat';
import DropdownItem from '../DropdownItem';

import AlertsIcon from 'zignal-landscape/lib/Icon/AlertsIcon';
import BriefingIcon from 'zignal-landscape/lib/Icon/BriefingIcon';
import UserIcon from 'zignal-landscape/lib/Icon/UserIcon';
import ExportIcon from 'zignal-landscape/lib/Icon/ExportIcon';
import DashboardIcon from 'zignal-landscape/lib/Icon/DashboardIcon';
import PeakIcon from 'zignal-landscape/lib/Icon/PeakIcon';
import WarningIcon from 'zignal-landscape/lib/Icon/WarningIcon';
import ImportDataIcon from 'zignal-landscape/lib/Icon/ImportDataIcon';
import ReadingListIcon from 'zignal-landscape/lib/Icon/ReadingListIcon';
import ReportIcon from 'zignal-landscape/lib/Icon/ReportIcon';
import News2Icon from 'zignal-landscape/lib/Icon/News2Icon';
import InformationIcon from 'zignal-landscape/lib/Icon/InformationIcon';

const buttonTextByType = {
  'csv_export': 'Download',
  'early_warning': 'Go To Dashboard',
};

// every supported Notification type *must* have an Icon
//   otherwise it is considered unsupported
//   @see isSupportedNotification

const iconsByType = {
  alert: AlertsIcon,
  briefing_disabled: BriefingIcon,
  campaign_assigned: UserIcon,
  csv_export: ExportIcon,
  dashboard_shared: DashboardIcon,
  early_warning: PeakIcon,
  event: WarningIcon,
  historic_import_completed: ImportDataIcon,
  instagram_acct_expired: AlertsIcon,
  reading_list_shared: ReadingListIcon,
  report_generated: ReportIcon, // all "Report"s, including Dashboard & Widget images
  report_disabled: ReportIcon,
  report_shared: ReportIcon,
  search_shared: News2Icon,
  tier_overrides_completed: InformationIcon
};

const labelsByType = {
  alert: 'Newsroom Alert Triggered',
  briefing_disabled: 'Briefing Disabled',
  campaign_assigned: "You've been assigned to the",
  csv_export: 'Export Generated',
  dashboard_shared: 'Dashboard Shared',
  early_warning: 'Early Warning Detected',
  historic_import_completed: 'Historic Data Import Completed',
  instagram_acct_expired: 'Instagram Account Expired',
  reading_list_shared: 'Reading List Shared',
  report_disabled: 'Report Disabled',
  report_shared: 'Report Shared',
  search_shared: 'Newsroom Saved Search Shared',
  tier_overrides_completed: 'Tier Overrides Completed'
};

// for { type: 'report_generated' }
const reportTypeText = {
  report: 'Report',
  widget: 'Widget',
  dashboard: 'Dashboard',
  report_widget: 'Widget',
};

export function getSubtitle(type, parameters) {
  let instagramAcctMessage;
  switch(type) {
    case 'alert': {
      const humanized = moment.duration(parameters.timeframe, 'seconds')
        .humanize()
        .replace('a ', '') // "a minute" --> "minute"
        .replace('an ', '') // "an hour" --> "hour"
      ;
      return `${parameters.totalStories} stories in the last ${humanized}`;
    }
    case 'briefing_disabled':
      return `${parameters.name} was not able to be processed and was disabled.`;
    case 'csv_export':
      return `for Dataset(s) in ${parameters.campaignName}`;
    case 'report_disabled':
      return `${parameters.name} was not able to be processed and was disabled.`;
    case 'report_generated':
      return `for ${parameters.profileName}`;
    case 'historic_import_completed':
      return `for ${parameters.campaignName}`;
    case 'instagram_acct_expired':
      instagramAcctMessage = parameters.instagramUsername ? ` @${parameters.instagramUsername}`:'';
      return `Click here to reauthenticate${instagramAcctMessage}`;
    case 'early_warning':
      return `The conversation is increasing for "${parameters.profileName}", `
            +`with ${parameters.storyVolume} stories at this time`;
    case 'tier_overrides_completed':
      return 'Reach scores have been updated on all stories.';
    default:
      return ''; // marked as `isRequired`
  }
}

export function getFromNowTimestamp(timestamp) {
  return moment(timestamp).fromNow()
    .replace('seconds', 'sec')
    .replace('minutes', 'min')
    .replace('hours', 'hrs')
    .replace('months', 'mos')
    .replace('years', 'yrs')
  ;
}

export function getTitle(type, parameters) {
  switch(type) {
    case 'alert':
      return parameters.name;
    case 'report_generated':
      return parameters.reportName;
    default:
      return ''; // marked as `isRequired`
  }
}

function getButtonText(type, parameters) {
  switch(type) {
    case 'report_generated':
      return `Download ${reportTypeText[parameters.reportType]}`;
    default:
      return buttonTextByType[type];
  }
}

export function getLabel(type, parameters) {
  switch(type) {
    case 'report_generated':
      return `${reportTypeText[parameters.reportType]} Generated`;
    default:
      return labelsByType[type] || ''; // marked as `isRequired`
  }
}

export function getUrl(type, parameters, timezoneOffset){
  switch(type){
    case 'early_warning':{

      const now = moment();
      const startTime = moment.utc(parameters.triggeredAt*1000) // seconds to millis
        .startOf('hour');
      const endTime = moment(startTime).add(1, 'hour');

      // expressed with User's timezone
      const lowerLimit = convertMomentToDatePickerTime(startTime.add(timezoneOffset, 'seconds'));
      let upperLimit;

      if(endTime.isSameOrBefore(now)){
        upperLimit = convertMomentToDatePickerTime(endTime.add(timezoneOffset, 'seconds'));
      }else{
        upperLimit = 'now';
      }

      return '/dashboard'
            +`?candidate=${parameters.profileId}`
            +`&start_time=${lowerLimit}`
            +`&end_time=${upperLimit}`
              .replace(/\n/gm, ' \b');
    }

    default:
      return parameters.url;
  }
}

function mapNotificationDataToProps(notification, timezoneOffset, unreadCount, index) {
  const { id, type, createdAt } = notification;
  let { parameters } = notification;
  const title = getTitle(type, parameters);

  if (type === 'tier_overrides_completed' && !parameters) {
    parameters = {};
  }

  return {
    buttonText: getButtonText(type, parameters),
    icon: iconsByType[type],
    isUnread: index < unreadCount,
    key: id,
    label: getLabel(type, parameters),
    subtitle: getSubtitle(type, parameters),
    timestamp: getFromNowTimestamp(createdAt),
    title: title ? title: '',
    url: getUrl(type, parameters, timezoneOffset),
  };
}

function isSupportedNotification({ type, parameters }) {
  const hasValidType = iconsByType[type] !== undefined;
  return (hasValidType && typeof parameters === 'object') ||
    (type === 'tier_overrides_completed');
}

const Dropdown = ({
  styles,
  timezoneOffset,
  isOpen,
  notifications,
  unreadCount,
  loadMore,
  total,
  offset,
  isNewNav = false,
  navigate = noop
}) => {
  if(!isOpen) {
    return null;
  }

  const dropdownWrapperClasses = {
    [styles.dropdownWrapper]: true,
    [styles.dropdownWrapperNewNav]: isNewNav
  };

  const dropdownContentClasses = {
    [styles.dropdownContent]: true,
    [styles.dropdownContentArrow]: !isNewNav
  };

  let moreButton;
  let listItems;
  let filteredNotifications = [];
  let disableMoreButton = false;
  if (Array.isArray(notifications) && notifications.length > 0) {
    filteredNotifications = notifications.filter(isSupportedNotification);
  }

  if (filteredNotifications.length > 0) {
    listItems = filteredNotifications.map((notification, index) => {
      const notificationProps = mapNotificationDataToProps(notification, timezoneOffset, unreadCount, index);
      return <DropdownItem {...notificationProps} isNewNav={isNewNav} navigate={navigate} />;
    });

    if(total <= offset) {
      disableMoreButton = true;
    }

    moreButton = (<button className={styles.loadMore} disabled={disableMoreButton} onClick={loadMore}>
      <span>More</span>
    </button>);
  } else {
    listItems = (<li className={styles.emptyMessage}>
      You don't have any notifications yet.
    </li>);

    moreButton = null;
  }

  return (
    <div className={classnames(dropdownWrapperClasses)}>
      <ul className={classnames(dropdownContentClasses)}>
        {listItems}
        {moreButton}
      </ul>
    </div>
  );
};

Dropdown.propTypes = {
  styles: PropTypes.object,
  timezoneOffset: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  loadMore: PropTypes.func.isRequired,
  notifications: PropTypes.array.isRequired,
  unreadCount: PropTypes.number.isRequired,
  total: PropTypes.number,
  offset: PropTypes.number,
  isNewNav: PropTypes.bool,
  navigate: PropTypes.func
};

Dropdown.displayName = 'Dropdown';

export default themeable(Dropdown);
